import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { LOG_LIST_PANEL_PREVIEW_IMAGE, PANEL_CATEGORIES } from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { useQueryLoader } from '../../data-table/Loaders';
import {
    businessServiceFilterId,
    siteFilterId,
    testFilterId,
    interfaceFilterId,
    networkDeviceFilterId,
    timeRangeFilterId,
    alertCategoryFilterId,
    severityFilterId,
    alertStateFilterId,
} from '../../filters';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { LegacyIcon } from '../../icon';
import { alertDescriptionRenderer } from './AlertDescription';
import { getStatusName } from '../../../utilities/StatusUtilities';
import { CUSTOM_ID } from '../../filters/time-range-filter/TimeRangeFilterConstants';

const title = t`Alerts`;

const dataSource = {
    url: '/query/alarmList',
    params: {
        entityFields: ['name'],
        metrics: [
            'queryId',
            'severity',
            'status',
            'entity',
            'beginTime',
            'duration',
            'desc',
            'linkSelection0',
            'linkSelection1',
        ],
    },
};

const detailLinks = {
    'Business Service': {
        page: 'business-service-detail',
        filterId: businessServiceFilterId,
    },
    Site: {
        page: 'site-detail',
        filterId: siteFilterId,
    },
    'Service Test': {
        page: 'test-detail',
        filterId: testFilterId,
    },
    Interface: {
        page: 'interface-detail',
        filterId: interfaceFilterId,
    },
    Device: {
        page: 'device-detail',
        filterId: networkDeviceFilterId,
    },
};

const nameLinker = (row) => {
    const { page, filterId } = detailLinks[row?.category];
    return {
        target: `/${page}`,
        mappedFilterParams: {
            [filterId]: 'entity',
            [timeRangeFilterId]: (data) => {
                return { id: CUSTOM_ID, start: data.beginTime, end: data.endTime };
            },
        },
    };
};

const columns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Start Time`,
        headerTooltip: t`Problem start time`,
        sortable: true,
        mapping: 'beginTime',
        width: 220,
        formatter: FORMATTER_TYPES.DATETIME,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Duration`,
        headerTooltip: t`Problem duration`,
        tooltip: true,
        sortable: true,
        mapping: 'duration',
        width: 100,
    },
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Severity`,
        headerTooltip: t`Problem severity is Critical, Degraded, or Excessive`,
        sortable: true,
        mapping: 'severity',
        width: 100,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`State`,
        headerTooltip: t`Problem state`,
        sortable: true,
        mapping: 'status',
        width: 100,
    },
    {
        type: COLUMN_TYPES.ICON,
        title: '',
        csvTitle: t`Alert Type`,
        csvFormatter: (value, row) => row?.entity?.iconTitle,
        icons: [
            {
                getIcon: (row) => <LegacyIcon icon={row?.entity?.icon} />,
                getTooltip: (row) => row?.entity?.iconTitle,
            },
        ],
        mapping: 'entity.icon',
        width: 30,
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Name`,
        headerTooltip: t`Problem name`,
        getTooltip: (row) => `${t`View details for`} ${row?.entity?.name}`,
        sortable: true,
        sortProperty: 'entity.name',
        mapping: 'entity.name',
        width: 180,
        link: nameLinker,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Description`,
        headerTooltip: t`Problem description`,
        tooltip: true,
        mapping: 'desc',
        width: 700,
        renderer: alertDescriptionRenderer,
    },
];

const sorters = [{ property: 'status', direction: 'asc' }];

function AlertListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return <ReportingDataTable panelProps={props} loader={loader} columns={columns} sorters={sorters} />;
}

AlertListPanel.propTypes = panelPropTypes;

AlertListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'AlarmList',
    catalogEntry: {
        category: PANEL_CATEGORIES.ALERT,
        title,
        description: t`List of alerts`,
        previewImage: LOG_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [alertCategoryFilterId, severityFilterId, alertStateFilterId],
        // This is a temporary solution to deal with the fact that the alert list uses different
        // values for the status filter than other pages do.  The proper solution is to fix the
        // APIs so that they all consistently expect the same values for the status filter.
        transformations: {
            [severityFilterId]: (state) => {
                return state.map((status) => getStatusName(status)?.toLowerCase());
            },
        },
    },
})(AlertListPanel);
